import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import TruckLoadingScene from '../../TruckLoadingScene';
import LoadingInstructionPanel from './LoadingInstructionPanel';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  Eye, 
  RefreshCw, 
  CheckCircle2, 
  AlertTriangle, 
  Package,
  Play,
  Menu,
  Truck,
  MapPin,
  Layers,
  AlertCircle,
  XCircle
} from 'lucide-react';

export default function Loader3DView({
  planData,
  selectedPalletId,
  setSelectedPalletId,
  onPalletPlaced,
  onPalletMarkedProblem,
  onRerunSolver,
  isMobile = false
}) {
  const [showPlacementConfirm, setShowPlacementConfirm] = useState(null);
  const [showMarkingDialog, setShowMarkingDialog] = useState(null);
  const [animatingPlacement, setAnimatingPlacement] = useState(false);
  const [sceneLoading, setSceneLoading] = useState(true);
  // Toggle between loading view (only next+placed) and full view (all pallets)
  const [fullLoadVisible, setFullLoadVisible] = useState(false);
  // Mobile: toggle expanded loading instructions
  const [mobileInstructionsExpanded, setMobileInstructionsExpanded] = useState(false);
  // Mobile: toggle 3D view visibility
  const [mobile3DVisible, setMobile3DVisible] = useState(false);
  // Mobile: show supporting pallet (for stacking)
  const [showStackOn, setShowStackOn] = useState(false);
  const truckSceneRef = useRef(null);

  // Handle 3D scene loading
  useEffect(() => {
    const timer = setTimeout(() => setSceneLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Issue reasons (same as in LoaderPalletList for consistency)
  const issueReasons = [
    { value: 'missing', label: 'Missing', icon: '❌', color: 'red', description: 'Pallet not found in warehouse' },
    { value: 'too_tall', label: 'Too Tall', icon: '📏', color: 'orange', description: 'Exceeds height limitations' },
    { value: 'not_toploadable', label: 'Not Top-loadable', icon: '⚠️', color: 'yellow', description: 'Cannot have items stacked on top due to fragility' },
    { value: 'damaged', label: 'Damaged', icon: '💥', color: 'red', description: 'Physical damage prevents safe loading' },
    { value: 'oversized', label: 'Oversized', icon: '📐', color: 'orange', description: 'Too large for available space' },
    { value: 'weight_limit', label: 'Weight Limit', icon: '⚖️', color: 'red', description: 'Exceeds weight capacity' },
    { value: 'quality_hold', label: 'Quality Hold', icon: '🔍', color: 'purple', description: 'Quality control requires inspection before loading' },
    { value: 'wrong_product', label: 'Wrong Product', icon: '🔄', color: 'blue', description: 'Incorrect product or specification' },
    { value: 'customer_request', label: 'Customer Request', icon: '📞', color: 'blue', description: 'Customer requested to exclude this item' },
    { value: 'other', label: 'Other Issue', icon: '❓', color: 'gray', description: 'Other issue preventing loading' }
  ];

  // Get next pallet ID first
  const nextPalletId = useMemo(() => {
    if (!planData?.stacks) return null;
    
    const currentStack = planData.stacks[planData?.progress?.currentStackIndex];
    if (!currentStack) return null;

    const nextInStack = currentStack.pallets?.find(p => 
      !planData.placedPallets?.includes(p.id) && 
      !planData.problemPallets?.some(pp => pp.id === p.id)
    );

    return nextInStack?.id || null;
  }, [planData]);

  // Convert planData to TruckLoadingScene format
  const trucksData = useMemo(() => {
    if (!planData || !planData.pallets) {
      return [{
        name: "Loading Truck",
        vehicleType: "trailer",
        pallets: []
      }];
    }

    // Convert pallets to TruckLoadingScene format
    const convertedPallets = planData.pallets.map((pallet, index) => {
      const isPlaced = planData.placedPallets?.includes(pallet.id) || false;
      const hasProblem = planData.problemPallets?.some(p => p.id === pallet.id) || false;
      const isNext = nextPalletId === pallet.id;

      // Ensure position is valid and within truck bounds
      let position = pallet.position;
      
      // Convert position from object format {x, y, z} to array format [x, y, z] if needed
      if (position && typeof position === 'object' && !Array.isArray(position)) {
        position = [position.x || 0, position.y || 0, position.z || 0];
      }
      
      const palletDepth = pallet.dimensions?.depth || 1.2;
      const palletWidth = pallet.dimensions?.width || 0.8;
      const palletHeight = pallet.dimensions?.height || 0.15;
      
      if (!Array.isArray(position) || position.length < 3) {
        // Default positioning if solver hasn't placed yet - ensure within bounds
        const stackIndex = index % 3; // Spread across 3 columns
        const rowIndex = Math.floor(index / 3);
        
        // Use actual truck dimensions from planData
        const truckWidth = planData.truckDimensions?.width || 2.45;
        const truckDepth = planData.truckDimensions?.depth || 12;
        
        // X: Center in truck width, accounting for pallet width
        const maxX = (truckWidth - palletWidth) / 2;
        const xPos = Math.max(-maxX, Math.min(maxX, (stackIndex - 1) * 0.7));
        
        // Z: Position from back to front, ensuring no pallet overlap
        const totalRows = Math.ceil(planData.pallets.length / 3);
        
        let zPos;
        if (totalRows === 1) {
          // Single row: center it
          zPos = 0;
        } else {
          // Multiple rows: position them so pallet edges don't overlap
          // Calculate positions by stacking pallets from back to front with proper gaps
          
          const truckHalfDepth = truckDepth / 2;
          const palletHalfDepth = palletDepth / 2;
          const minGap = 0.05; // 5cm minimum gap between pallets
          
          // Start from the back wall and place pallets one by one
          // First row: back edge touches truck back boundary
          const firstRowZ = -(truckHalfDepth - palletHalfDepth);
          
          if (rowIndex === 0) {
            // First row at the back
            zPos = firstRowZ;
          } else {
            // Subsequent rows: previous row back edge + pallet depth + gap + half current pallet depth
            const previousRowBackEdge = firstRowZ + (rowIndex - 1) * (palletDepth + minGap);
            zPos = previousRowBackEdge + palletDepth + minGap + palletHalfDepth;
            
            // Ensure we don't exceed front boundary
            const frontBoundary = truckHalfDepth - palletHalfDepth;
            if (zPos > frontBoundary) {
              // If we run out of space, compress spacing
              const totalNeededDepth = totalRows * palletDepth + (totalRows - 1) * minGap;
              const availableDepth = truckDepth - palletDepth; // Available for centers
              
              if (totalNeededDepth > availableDepth) {
                // Use proportional spacing if pallets don't fit with gaps
                const compressedSpacing = (availableDepth - palletDepth) / (totalRows - 1);
                zPos = firstRowZ + rowIndex * compressedSpacing;
              } else {
                zPos = frontBoundary; // Clamp to boundary
              }
            }
          }
          
          // Debug logging
          console.log(`🔧 Pallet ${pallet.id} (row=${rowIndex}/${totalRows}): depth=${palletDepth}m, Z=${zPos.toFixed(3)}`);
          console.log(`🔧 Edges: back=${(zPos-palletHalfDepth).toFixed(3)} to front=${(zPos+palletHalfDepth).toFixed(3)}`);
        }
        
        position = [
          xPos, // X: centered across width
          palletHeight / 2 + 0.05, // Y: bottom of pallet just above ground
          zPos // Z: safely within truck depth
        ];
        
        console.log(`✅ Pallet ${pallet.id} DEFAULT position: [${position.map(p => p.toFixed(2)).join(', ')}]`);
      } else {
        // Use solver positions directly - no conversion needed since solver now uses correct truck dimensions
        console.log(`🤖 Using SOLVER position: [${position.map(p => p.toFixed(2)).join(', ')}]`);
      }

      // Determine visual attribute based on status
      let attribute = pallet.attribute || 'none';
      if (hasProblem) {
        attribute = 'dangerous'; // Red for problems
      } else if (isNext) {
        attribute = 'fragile'; // Yellow/orange for next to load
      } else if (isPlaced) {
        attribute = 'none'; // Green for placed
      }

      const finalPallet = {
        id: pallet.id,
        name: pallet.name || `Pallet${pallet.id}`,
        position: position,
        rotation: pallet.rotation || 0,
        dimensions: pallet.dimensions || { width: 0.8, depth: 1.2, height: 0.15 },
        attribute: attribute,
        notToploadable: Boolean(pallet.notToploadable),
        weight: pallet.weight || 0,
        locked: isPlaced, // Placed pallets are locked
        unit_id: 1,
        // Preserve image URLs from scanner
        imageUrl: pallet.imageUrl || pallet.image_url,
        labelImageUrl: pallet.labelImageUrl || pallet.label_image_url,
        // Preserve other metadata
        destination: pallet.destination,
        shipment_id: pallet.shipment_id,
        stacking_class: pallet.stacking_class || pallet.stackingClass,
        handling_tips: pallet.handling_tips || pallet.handlingTips,
      };
      
      console.log(`🎯 FINAL Pallet ${pallet.id}: position=[${position.map(p => p.toFixed(2)).join(', ')}], dimensions=[${finalPallet.dimensions.width}x${finalPallet.dimensions.depth}x${finalPallet.dimensions.height}]`);
      
      return finalPallet;
    });

    return [{
      name: "Loading Truck",
      vehicleType: planData.truckDimensions?.vehicleType || "trailer",
      pallets: convertedPallets
    }];
  }, [planData, nextPalletId]);

  // Get selected pallet details
  const selectedPallet = useMemo(() => {
    if (!selectedPalletId || !trucksData[0]) return null;
    return trucksData[0].pallets.find(p => p.id === selectedPalletId);
  }, [selectedPalletId, trucksData]);

  // Get next pallet to place
  const nextPallet = useMemo(() => {
    if (!trucksData[0] || !nextPalletId) return null;
    return trucksData[0].pallets.find(p => p.id === nextPalletId);
  }, [trucksData, nextPalletId]);

  // Handle pallet placement
  const handlePlacePallet = async (palletId) => {
    setAnimatingPlacement(true);
    setShowPlacementConfirm(null);
    
    // Simulate placement animation
    setTimeout(() => {
      onPalletPlaced(palletId);
      setAnimatingPlacement(false);
    }, 1000);
  };

  // Handle marking pallet with issue
  const handleMarkPallet = (palletId, reason) => {
    onPalletMarkedProblem(palletId, reason);
    setShowMarkingDialog(null);
    setSelectedPalletId(null);
  };

  // Quick mark issue (no dialog, direct mark)
  const handleQuickMarkIssue = useCallback((palletId, reason) => {
    onPalletMarkedProblem(palletId, reason);
    setSelectedPalletId(null);
  }, [onPalletMarkedProblem, setSelectedPalletId]);

  // Quick load confirm (shows confirmation dialog)
  const handleQuickLoadConfirm = useCallback((palletId) => {
    setShowPlacementConfirm(palletId);
  }, []);

  // Handle pallet selection from 3D scene
  const handlePalletSelection = (palletId) => {
    setSelectedPalletId(palletId);
  };

  // Render dialogs function
  const renderDialogs = () => {
    return (
      <>
        {/* Placement Confirmation Dialog */}
        {showPlacementConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                  <h3 className="text-lg font-semibold">Confirm Placement</h3>
                </div>
                
                <p className="text-gray-700 mb-6">
                  Are you ready to mark this pallet as loaded?
                </p>
                
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowPlacementConfirm(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handlePlacePallet(showPlacementConfirm)}
                    disabled={animatingPlacement}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {animatingPlacement ? 'Loading...' : 'Confirm Load'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Issue Marking Dialog */}
        {showMarkingDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md max-h-[80vh] overflow-hidden">
              <div className="bg-red-50 border-b border-red-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <h3 className="text-lg font-semibold text-red-900">Mark Issue</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowMarkingDialog(null)}
                  >
                    ×
                  </Button>
                </div>
              </div>
              
              <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
                <div className="grid gap-2">
                  {issueReasons.map((reason) => (
                    <Button
                      key={reason.value}
                      variant="outline"
                      className={`p-3 h-auto justify-start text-left hover:border-red-300 hover:bg-red-50`}
                      onClick={() => handleMarkPallet(showMarkingDialog, reason.value)}
                    >
                      <div className="flex items-start space-x-3">
                        <span className="text-lg">{reason.icon}</span>
                        <div className="flex-1">
                          <div className="font-medium">{reason.label}</div>
                          <div className="text-sm text-gray-600">{reason.description}</div>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  if (isMobile) {
    // Mobile layout: Focus on pallet image and instructions, 3D view hidden by default
    return (
      <div className="flex-1 flex flex-col bg-gray-50 max-w-full overflow-x-hidden">
        {/* Minimal Header - single row */}
        <div className="bg-white border-b border-gray-200 px-3 py-1.5 flex items-center justify-between">
          <div className="flex items-center space-x-2 min-w-0">
            <span className="text-sm font-semibold text-gray-900">
              {planData?.progress?.placedCount || 0}/{planData?.progress?.totalPallets || 0}
            </span>
            <span className="text-xs text-gray-400">loaded</span>
          </div>
          
          <div className="flex items-center space-x-1.5 flex-shrink-0">
            {nextPallet?.position?.[1] > 0.3 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowStackOn(true)}
                className="px-2 h-7 text-xs bg-amber-500 hover:bg-amber-600 border-amber-500 text-white"
              >
                <Layers className="h-3 w-3 mr-1" />
                Stack
              </Button>
            )}
            <Button
              size="sm"
              variant={mobile3DVisible ? "default" : "outline"}
              onClick={() => setMobile3DVisible(v => !v)}
              className="px-2 h-7 text-xs"
            >
              <Truck className="h-3 w-3 mr-1" />
              3D
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onRerunSolver}
              className="px-1.5 h-7 text-xs"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* 3D Scene - only visible when toggled */}
        {mobile3DVisible && (
          <div className="h-64 relative bg-gray-900 border-b-4 border-blue-500">
            {sceneLoading && (
              <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-10">
                <div className="text-center text-white">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-sm">Loading 3D...</p>
                </div>
              </div>
            )}
            
            <TruckLoadingScene
              ref={truckSceneRef}
              lines={[]}
              setLines={() => {}}
              activeLineId={null}
              setActiveLineId={() => {}}
              trucks={trucksData}
              setTrucks={() => {}}
              activeTruckIndex={0}
              setActiveTruckIndex={() => {}}
              unplacedPallets={[]}
              setUnplacedPallets={() => {}}
              truckId={1}
              vehicleType={planData?.truckDimensions?.vehicleType || "trailer"}
              truckName="Loading Truck"
              onSelectedPalletChange={handlePalletSelection}
              showGhostsOnly={false}
              loadingMode={!fullLoadVisible}
              nextPalletId={nextPallet?.id || nextPalletId}
            />
            
            {/* Close button overlay */}
            <button
              onClick={() => setMobile3DVisible(false)}
              className="absolute top-2 right-2 bg-black/50 text-white p-1.5 rounded-full"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Stack On Popup Modal */}
        {showStackOn && nextPallet?.position?.[1] > 0.3 && (() => {
          // Find the pallet directly below using stack structure
          // Within a stack, pallets are ordered by loading order (bottom first)
          let stackOnPallet = null;
          let stackIndex = -1;
          let palletIndexInStack = -1;
          
          // Find which stack contains the next pallet
          if (planData?.stacks) {
            for (let si = 0; si < planData.stacks.length; si++) {
              const stack = planData.stacks[si];
              const palletIdx = stack.pallets?.findIndex(p => p.id === nextPallet.id);
              if (palletIdx !== undefined && palletIdx > 0) {
                // Found the pallet, and it's not the first in the stack
                // The pallet directly below is the previous one in the stack
                stackOnPallet = stack.pallets[palletIdx - 1];
                stackIndex = si;
                palletIndexInStack = palletIdx;
                break;
              }
            }
          }
          
          // Fallback: use position-based matching if stack lookup fails
          if (!stackOnPallet) {
            const allPallets = planData?.pallets || [];
            const supportingPallets = allPallets.filter(p => {
              if (p.id === nextPallet.id) return false;
              const xMatch = Math.abs((p.position?.[0] || 0) - (nextPallet.position?.[0] || 0)) < 0.4;
              const zMatch = Math.abs((p.position?.[2] || 0) - (nextPallet.position?.[2] || 0)) < 0.6;
              const yBelow = (p.position?.[1] || 0) < (nextPallet.position?.[1] || 0);
              return xMatch && zMatch && yBelow;
            }).sort((a, b) => (b.position?.[1] || 0) - (a.position?.[1] || 0));
            stackOnPallet = supportingPallets[0];
          }
          
          const truckWidth = planData?.truckDimensions?.width || 2.45;
          const truckDepth = planData?.truckDimensions?.depth || 12;
          
          // Calculate position info for supporting pallet
          const getPositionInfo = (pallet) => {
            const x = pallet?.position?.[0] || 0;
            const z = pallet?.position?.[2] || 0;
            const y = pallet?.position?.[1] || 0;
            const laneThird = truckWidth / 3;
            const lane = x < -laneThird / 2 ? 'Left' : x > laneThird / 2 ? 'Right' : 'Center';
            const rowNum = Math.floor((z + truckDepth / 2) / 1.2) + 1;
            const level = y > 0.3 ? `Level ${Math.round(y / 1.2) + 1}` : 'Floor';
            return { lane, rowNum, level };
          };
          
          const posInfo = stackOnPallet ? getPositionInfo(stackOnPallet) : null;
          
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowStackOn(false)}>
              <div className="bg-white rounded-2xl shadow-2xl mx-4 max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="bg-amber-500 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-white">
                    <Layers className="h-5 w-5" />
                    <span className="font-semibold">Stack on top of</span>
                  </div>
                  <button onClick={() => setShowStackOn(false)} className="text-white/80 hover:text-white">
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
                
                {stackOnPallet ? (
                  <div className="p-4">
                    {/* Large Image */}
                    <div className="relative aspect-square max-h-56 mx-auto rounded-xl overflow-hidden bg-amber-50 border-2 border-amber-200 mb-4">
                      {(stackOnPallet.imageUrl || stackOnPallet.labelImageUrl) ? (
                        <img 
                          src={stackOnPallet.imageUrl || stackOnPallet.labelImageUrl}
                          alt={`Pallet ${stackOnPallet.name || stackOnPallet.id}`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <Package className="h-16 w-16 text-amber-300 mb-2" />
                          <span className="text-sm text-amber-400">No image</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Pallet Name */}
                    <h3 className="text-xl font-bold text-gray-900 text-center mb-3">
                      {stackOnPallet.name || `Pallet #${stackOnPallet.id}`}
                    </h3>
                    
                    {/* Position Grid */}
                    <div className="grid grid-cols-3 gap-2 text-center mb-3">
                      <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                        <div className="text-xs text-amber-600 font-medium">Lane</div>
                        <div className="font-bold text-amber-900">{posInfo?.lane}</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                        <div className="text-xs text-amber-600 font-medium">Row</div>
                        <div className="font-bold text-amber-900">{posInfo?.rowNum}</div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                        <div className="text-xs text-amber-600 font-medium">Level</div>
                        <div className="font-bold text-amber-900">{posInfo?.level}</div>
                      </div>
                    </div>
                    
                    {/* Dimensions */}
                    <p className="text-sm text-gray-500 text-center">
                      {stackOnPallet.dimensions?.width?.toFixed(1)}×{stackOnPallet.dimensions?.depth?.toFixed(1)}×{stackOnPallet.dimensions?.height?.toFixed(1)}m
                      {stackOnPallet.weight && <span className="ml-2">• {stackOnPallet.weight}kg</span>}
                    </p>
                    
                    {palletIndexInStack > 1 && (
                      <p className="text-xs text-amber-600 text-center mt-2">
                        {palletIndexInStack - 1} more pallet{palletIndexInStack > 2 ? 's' : ''} below in this stack
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <Truck className="h-12 w-12 text-blue-400 mx-auto mb-2" />
                    <p className="text-blue-700 font-medium">Place on {planData?.truckDimensions?.vehicleType === 'truck' ? 'truck' : 'trailer'} floor</p>
                    <p className="text-sm text-gray-500 mt-1">This pallet goes directly on the floor</p>
                  </div>
                )}
                
                {/* Close button */}
                <div className="px-4 pb-4">
                  <Button
                    onClick={() => setShowStackOn(false)}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    Got it
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Main content: Pallet info */}
        {nextPallet ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Large Pallet Image - hidden when 3D view is visible */}
            {!mobile3DVisible && (
              <div className="flex-shrink-0 bg-white p-4 border-b">
                <div className="relative aspect-square max-h-48 mx-auto rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200">
                  {(nextPallet.imageUrl || nextPallet.labelImageUrl) ? (
                    <img 
                      src={nextPallet.imageUrl || nextPallet.labelImageUrl}
                      alt={`Pallet ${nextPallet.name || nextPallet.id}`}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`absolute inset-0 flex flex-col items-center justify-center ${(nextPallet.imageUrl || nextPallet.labelImageUrl) ? 'hidden' : ''}`}>
                    <Package className="h-16 w-16 text-gray-300 mb-2" />
                    <span className="text-sm text-gray-400">No image</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Pallet Details - add bottom padding for fixed button */}
            <div className="flex-1 overflow-y-auto bg-gray-50 pb-36">
              <div className="p-4 space-y-3">
                {/* Name and basic info */}
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center space-x-2 flex-wrap">
                        <h3 className="text-lg font-bold text-gray-900">{nextPallet.name}</h3>
                        {nextPallet.isImportedPallet && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Imported
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {nextPallet.dimensions?.width?.toFixed(1)}×{nextPallet.dimensions?.depth?.toFixed(1)}×{nextPallet.dimensions?.height?.toFixed(1)}m
                        {nextPallet.weight && <span className="ml-2 font-medium">{nextPallet.weight}kg</span>}
                      </div>
                    </div>
                    {nextPallet.attribute && nextPallet.attribute !== 'none' && (
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                        nextPallet.attribute === 'dangerous' ? 'bg-red-100 text-red-700' :
                        nextPallet.attribute === 'medicine' ? 'bg-purple-100 text-purple-700' :
                        nextPallet.attribute === 'food' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {nextPallet.attribute}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Position Card */}
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-gray-900">Placement</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    {(() => {
                      const truckWidth = planData?.truckDimensions?.width || 2.45;
                      const x = nextPallet.position?.[0] || 0;
                      const laneThird = truckWidth / 3;
                      const lane = x < -laneThird / 2 ? 'Left Lane' : x > laneThird / 2 ? 'Right Lane' : 'Center Lane';
                      const truckDepth = planData?.truckDimensions?.depth || 12;
                      const z = nextPallet.position?.[2] || 0;
                      const rowNum = Math.floor((z + truckDepth / 2) / 1.2) + 1;
                      const y = nextPallet.position?.[1] || 0;
                      const level = y > 0.3 ? `Level ${Math.round(y / 1.2) + 1}` : 'Floor';
                      return (
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-xs text-gray-500">Lane</div>
                            <div className="font-semibold">{lane.replace(' Lane', '')}</div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-xs text-gray-500">Row</div>
                            <div className="font-semibold">{rowNum}</div>
                          </div>
                          <div className="bg-gray-50 rounded p-2">
                            <div className="text-xs text-gray-500">Level</div>
                            <div className="font-semibold">{level}</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                
                {/* Stacking info if above floor */}
                {nextPallet.position?.[1] > 0.3 && (
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                    <div className="flex items-center space-x-2 mb-1">
                      <Layers className="h-4 w-4 text-amber-600" />
                      <span className="font-medium text-amber-900">Stack on top of:</span>
                    </div>
                    <div className="text-sm text-amber-800">
                      {(() => {
                        const allPallets = planData?.pallets || [];
                        const supporting = allPallets.filter(p => {
                          if (p.id === nextPallet.id) return false;
                          const xMatch = Math.abs((p.position?.[0] || 0) - (nextPallet.position?.[0] || 0)) < 0.4;
                          const zMatch = Math.abs((p.position?.[2] || 0) - (nextPallet.position?.[2] || 0)) < 0.6;
                          const yBelow = (p.position?.[1] || 0) < (nextPallet.position?.[1] || 0);
                          return xMatch && zMatch && yBelow;
                        });
                        return supporting.length > 0 
                          ? supporting.map(p => p.name || `Pallet #${p.id}`).join(', ')
                          : 'Previously placed pallets';
                      })()}
                    </div>
                  </div>
                )}
                
                {/* Handling Tips */}
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-gray-900">Handling</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    {nextPallet.handling_tips || nextPallet.handlingTips || (() => {
                      const attr = nextPallet.attribute;
                      if (attr === 'dangerous') return '⚠️ Handle with care. Keep separated from food and medicine. Follow ADR regulations.';
                      if (attr === 'medicine') return '💊 Temperature sensitive. Handle gently. Keep away from dangerous goods.';
                      if (attr === 'food') return '🍎 Keep clean and dry. Separate from chemicals and dangerous goods.';
                      const sc = nextPallet.stacking_class || nextPallet.stackingClass;
                      if (sc === 'C0') return '❌ Do NOT stack anything on top! Very fragile contents.';
                      if (sc === 'C1') return '⚠️ Light items only on top (max 100kg). Handle with care.';
                      if (sc === 'C2') return 'Standard pallet. Can support medium weight stacking.';
                      return 'Standard handling. Center weight evenly on forks.';
                    })()}
                  </div>
                </div>
                
                {/* Warnings */}
                {nextPallet.notToploadable && (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      <span className="font-medium text-red-800">Nothing can be stacked on this pallet!</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center text-gray-400">
              <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium text-gray-700">All pallets loaded!</p>
              <p className="text-sm">Loading complete</p>
            </div>
          </div>
        )}

        {/* Fixed Bottom Actions - Always visible, never scrolls */}
        {nextPallet && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 shadow-lg z-50" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
            {/* Quick issue buttons */}
            <div className="flex space-x-2 mb-3">
              {['Missing', 'Damaged', 'Wrong'].map((reason) => (
                <button
                  key={reason}
                  onClick={() => handleQuickMarkIssue(nextPallet.id, reason.toLowerCase())}
                  disabled={animatingPlacement}
                  className="flex-1 text-xs py-2 px-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors font-medium"
                >
                  {reason}
                </button>
              ))}
              <button
                onClick={() => setShowMarkingDialog(nextPallet.id)}
                disabled={animatingPlacement}
                className="text-xs py-2 px-3 rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"
              >
                ⋯
              </button>
            </div>

            {/* Main load button */}
            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-lg h-14 font-bold rounded-xl shadow-lg"
              onClick={() => handleQuickLoadConfirm(nextPallet.id)}
              disabled={animatingPlacement}
            >
              <CheckCircle2 className="h-6 w-6 mr-2" />
              Confirm Loaded
            </Button>
          </div>
        )}

        {renderDialogs()}
      </div>
    );
  }

  // Desktop layout: split view
  return (
    <div className="flex-1 flex bg-gray-50 max-w-full overflow-x-hidden">
      {/* Left Panel: 3D Scene */}
      <div className="flex-1 flex flex-col bg-gray-900">
        {/* 3D Scene Header */}
        <div className="bg-gray-800 text-white p-3 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">3D Truck View</h3>
              <p className="text-sm text-gray-300">
                Stack {(planData?.progress?.currentStackIndex || 0) + 1} of {planData?.progress?.totalStacks || 1}
              </p>
            </div>
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="outline"
                className="text-white border-gray-600 hover:bg-gray-700"
                onClick={() => setFullLoadVisible(v => !v)}
              >
                <Eye className="h-4 w-4 mr-1" />
                {fullLoadVisible ? 'Show loading view' : 'Show full load'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-white border-gray-600 hover:bg-gray-700"
                onClick={() => setSelectedPalletId(nextPallet?.id || null)}
              >
                <Eye className="h-4 w-4 mr-1" />
                Focus Next
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-white border-gray-600 hover:bg-gray-700"
                onClick={onRerunSolver}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Replan
              </Button>
            </div>
          </div>
        </div>

        {/* 3D Scene */}
        <div className="flex-1 relative">
          {sceneLoading && (
            <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-10">
              <div className="text-center text-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                <p>Loading 3D View...</p>
              </div>
            </div>
          )}
          
          <TruckLoadingScene
            ref={truckSceneRef}
            lines={[]}
            setLines={() => {}}
            activeLineId={null}
            setActiveLineId={() => {}}
            trucks={trucksData}
            setTrucks={() => {}}
            activeTruckIndex={0}
            setActiveTruckIndex={() => {}}
            unplacedPallets={[]}
            setUnplacedPallets={() => {}}
            truckId={1}
            vehicleType={planData?.truckDimensions?.vehicleType || "trailer"}
            truckName="Loading Truck"
            onSelectedPalletChange={handlePalletSelection}
            showGhostsOnly={false}
            loadingMode={!fullLoadVisible}
            nextPalletId={nextPallet?.id || nextPalletId}
          />
        </div>
      </div>

      {/* Right Panel: Loading Instructions */}
      <div className="w-96 bg-white border-l border-gray-200 flex flex-col">
        <LoadingInstructionPanel
          pallet={nextPallet}
          allPallets={planData?.pallets || []}
          placedPallets={planData?.placedPallets || []}
          truckDimensions={planData?.truckDimensions}
          onLoadConfirm={handleQuickLoadConfirm}
          onMarkIssue={handleQuickMarkIssue}
          disabled={animatingPlacement}
        />
        
        {/* Progress Footer */}
        <div className="flex-shrink-0 bg-gray-100 border-t px-4 py-3">
          <div className="flex items-center justify-between text-sm">
            <div className="space-y-1">
              <div className="flex items-center space-x-4">
                <span className="text-green-600 font-medium">✓ {planData?.progress?.placedCount || 0} loaded</span>
                <span className="text-red-600">✗ {planData?.problemPallets?.length || 0} issues</span>
              </div>
              <div className="text-gray-500 text-xs">
                {(planData?.progress?.totalPallets || 0) - (planData?.progress?.placedCount || 0) - (planData?.problemPallets?.length || 0)} remaining
              </div>
            </div>
            <div className="text-right">
              <div className="font-medium">{planData?.truckDimensions?.vehicleType === "truck" ? "Truck" : "Trailer"}</div>
              <div className="text-xs text-gray-500">
                {planData?.truckDimensions?.depth || 12}m × {planData?.truckDimensions?.width || 2.45}m
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderDialogs()}
    </div>
  );
}